import { useCallback, useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";

import FormFieldSpace from "components/FormFields/Space";
import FormFieldTags from "components/FormFields/Tags";
import Button from "ds/components/Button";
import FormField from "ds/components/Form/Field";
import FormFieldHidden from "ds/components/Form/Field/Hidden";
import FullScreenModalBody from "ds/components/FullScreenModal/Body";
import FullScreenModalFooter from "ds/components/FullScreenModal/Footer";
import Input from "ds/components/Input";
import SecretInput from "ds/components/SecretInput";
import Textarea from "ds/components/Textarea";
import useTypedContext from "hooks/useTypedContext";
import { VcsProvider } from "types/generated";
import { stringIsRequired } from "utils/formValidators";
import { AccountContext } from "views/AccountWrapper";

import VCSIntegrationTypeFieldControl from "../../../components/IntegrationTypeFieldControl";
import VCSIntegrationsDocumentationButton from "../../components/DocumentationButton";
import VCSChecksToggle from "../../components/VCSChecksToggle";
import useCloseFullScreenModal from "../../useCloseFullScreenModal";
import {
  getFormDefaultValues,
  mapCreateIntegrationInputValues,
  mapUpdateIntegrationInputValues,
} from "./helpers";
import { ManageBitbucketCloudFormFieldsV2 } from "../types";
import useCheckBitbucketCloudHasDefaultIntegration from "../useCheckBitbucketCloudHasDefaultIntegration";
import useUpdateBitbucketCloudIntegrationV2 from "../useUpdateBitbucketCloudIntegrationV2";
import useCreateBitbucketCloudIntegrationV2 from "../useCreateBitbucketCloudIntegrationV2";
import GitCheckoutToggle from "../../components/GitCheckoutToggle";
import { BitbucketCloudIntegrationData } from "../Edit/gql";

type VCSIntegrationsManageBitbucketCloudFormProps = {
  integration?: BitbucketCloudIntegrationData;
  onIsDirtyChange: (value: boolean) => void;
};

const VCSIntegrationsManageBitbucketCloudFormV2 = ({
  integration,
  onIsDirtyChange,
}: VCSIntegrationsManageBitbucketCloudFormProps) => {
  const isEditMode = !!integration;
  const { viewer } = useTypedContext(AccountContext);

  const { handleOnClose } = useCloseFullScreenModal();

  const manageBitbucketCloudForm = useForm<ManageBitbucketCloudFormFieldsV2>({
    defaultValues: getFormDefaultValues(integration),
    mode: "onChange",
  });

  const {
    register,
    formState: { errors, isDirty },
    handleSubmit,
    setValue,
    trigger,
  } = manageBitbucketCloudForm;

  const { createBitbucketCloudIntegration, loading: createLoading } =
    useCreateBitbucketCloudIntegrationV2();
  const { updateBitbucketCloudIntegration, loading: updateLoading } =
    useUpdateBitbucketCloudIntegrationV2();

  const { hasDefaultIntegration, loading: checkDefaultIntegrationLoading } =
    useCheckBitbucketCloudHasDefaultIntegration(isEditMode);

  const withIntegrationTypeControl = !isEditMode && viewer.admin;
  const withSpaceControl = !withIntegrationTypeControl && !integration?.isDefault;

  const onHiddenToggle = useCallback(
    (isHidden: boolean) => {
      if (!isHidden) {
        setValue("apiToken", "", { shouldValidate: true });
      } else {
        // revalidate the whole form with hidden field
        trigger();
      }
    },
    [setValue, trigger]
  );

  const handleSetup = useCallback(
    () =>
      handleSubmit((formData: ManageBitbucketCloudFormFieldsV2) => {
        if (isEditMode) {
          updateBitbucketCloudIntegration(
            mapUpdateIntegrationInputValues(formData, integration.id),
            handleOnClose
          );
        } else {
          createBitbucketCloudIntegration(mapCreateIntegrationInputValues(formData), handleOnClose);
        }
      })(),
    [
      createBitbucketCloudIntegration,
      handleOnClose,
      integration?.id,
      isEditMode,
      updateBitbucketCloudIntegration,
      handleSubmit,
    ]
  );

  useEffect(() => {
    onIsDirtyChange(isDirty);
  }, [isDirty, onIsDirtyChange]);

  return (
    <FormProvider {...manageBitbucketCloudForm}>
      <FullScreenModalBody>
        <FormField
          label="Integration name"
          helperText="The integration name is unique and cannot be changed"
          error={errors?.name?.message}
          noMargin
        >
          {({ ariaInputProps }) => (
            <Input
              placeholder="Enter integration name here"
              error={!!errors?.name}
              {...register("name", {
                validate: stringIsRequired("Integration name field is required."),
              })}
              disabled={isEditMode}
              {...ariaInputProps}
            />
          )}
        </FormField>

        {withIntegrationTypeControl && (
          <VCSIntegrationTypeFieldControl
            hasAlreadyDefaultIntegration={hasDefaultIntegration}
            loading={checkDefaultIntegrationLoading}
            provider={VcsProvider.BitbucketCloud}
          />
        )}
        {withSpaceControl && <FormFieldSpace />}

        <FormField label="Email" error={errors?.email?.message}>
          {({ ariaInputProps }) => (
            <Input
              placeholder="Enter email here"
              error={!!errors?.email}
              {...register("email", {
                validate: stringIsRequired("Email field is required."),
              })}
              {...ariaInputProps}
            />
          )}
        </FormField>

        {!isEditMode && (
          <FormField label="API token" error={errors?.apiToken?.message}>
            {({ ariaInputProps }) => (
              <SecretInput
                placeholder="Enter API token here"
                error={!!errors?.apiToken}
                {...register("apiToken", {
                  validate: stringIsRequired("API token field is required."),
                })}
                {...ariaInputProps}
              />
            )}
          </FormField>
        )}

        {isEditMode && (
          <FormFieldHidden
            hiddenPlaceholder="Update API token"
            onHiddenToggle={onHiddenToggle}
            label="API token"
            error={errors?.apiToken?.message}
          >
            {(isHidden) => (
              <SecretInput
                placeholder="Enter API token here"
                error={!!errors?.apiToken}
                {...register("apiToken", {
                  ...(!isHidden && {
                    validate: stringIsRequired("API token field is required."),
                  }),
                })}
              />
            )}
          </FormFieldHidden>
        )}

        <GitCheckoutToggle disabled={true} />
        <VCSChecksToggle />

        <FormFieldTags label="Labels" tagName="label" name="labels" isOptional />

        <FormField label="Description" isOptional>
          {({ ariaInputProps }) => (
            <Textarea
              placeholder="Enter description here"
              {...register("description")}
              {...ariaInputProps}
            />
          )}
        </FormField>
      </FullScreenModalBody>
      <FullScreenModalFooter justify="between">
        <VCSIntegrationsDocumentationButton provider={VcsProvider.BitbucketCloud} />

        <Button onPress={handleSetup} variant="primary" loading={createLoading || updateLoading}>
          {isEditMode ? "Save" : "Set up"}
        </Button>
      </FullScreenModalFooter>
    </FormProvider>
  );
};

export default VCSIntegrationsManageBitbucketCloudFormV2;
